
// Types
import type {
  Cart,
  CartItem,
  MetaData as HandlePaymentCompleteMetaData,
  Payload as HandlePaymentCompletePayload,
  User as HandlePaymentCompleteUser,
  CancellationProtection,
} from '@white-label-types/parking-checkout';
import type { PusherEvent } from '@white-label-helper/pusher-handle-payment-request';
import type {
  ExtrasCancellationProtectionProduct,
  POI,
  CancellationPolicy,
  AmendmentPolicy,
} from '@white-label-types/parking-booking';

// Packages
import { defineComponent } from 'vue';
import { mapActions, mapState } from 'pinia';
import { Portal, PortalTarget } from 'portal-vue';

// Helpers
import {
  gaDataLayer,
  gaDataLayerLounges,
} from '@white-label-helper/ga-data-layer';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';
import { trackBeginCheckout } from '@white-label-helper/ga-tracking';
import { fetchCartItems } from '@white-label-helper/api-parking-cart';
import { handlePaymentHelper } from '@white-label-helper/pusher-handle-payment-request';
import { continueBookingHelper } from '@white-label-helper/pusher-continue-booking';
import { formatPrice } from '@white-label-helper/helper-payment';
import { isBookingPortal } from '@white-label-helper/is-booking-portal';
import { updateCancellationProtectionRequestSingleItem, updateCancellationProtectionRequestWholeCart } from '@white-label-helper/api-parkings-cart';
import { parseDiscountCodeData } from '@white-label-helper/discount-code-parser';
import { getFeatureFlag, identifyWithNewContext, isInExperiment } from '@white-label-plugin/launch-darkly';
import { addCustomMetric, usePartnerConfigHelper } from '@white-label-helper/shared-helpers';

// Mixins
import checkout from '@white-label-helper/mixin-checkout';

// Stores
import { usePreloaderControllerStore } from '@white-label-store/preloader-controller';
import { useDiscountCodeStore } from '@white-label-store/discount-code';
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';
import useDepositStore from '@white-label-store/deposits'
import { usePaymentFormStore } from '@white-label-store/payment-form';

// Constants
import {
  APP_HERO_PRODUCTS,
  ContinueBookingStatuses,
  PAYMENT_PROVIDERS,
  PRODUCT_TYPES,
  ROUTE_NAMES,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';

import { StatusCodes } from 'http-status-codes';

const {
  handlePayment,
  destroyHandlePayment,
  handleBookingOrderWithoutPayment,
} = handlePaymentHelper('original');
const { continueBooking: continueOriginalBooking, destroyContinueBoooking } =
  continueBookingHelper('original');

import PaymentPageNavigation from '../payment-page-navigation/payment-page-navigation.vue';
import PaymentForm from '../payment-form/payment-form.vue';
import PaymentFormPreloader from '../payment-form-preloader/payment-form-preloader.vue';
import PaymentSummary from '../payment-summary/payment-summary.vue';
import PaymentSummaryDropdown from '../payment-summary-dropdown/payment-summary-dropdown.vue';
import SummaryPreloader from '../summary-preloader/summary-preloader.vue';
import StickySidebar from '../sticky-sidebar/sticky-sidebar.vue';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';

export default defineComponent({
  name: 'PaymentPage',

  handlePayment,
  destroyHandlePayment,
  continueOriginalBooking,
  destroyContinueBoooking,
  handleBookingOrderWithoutPayment,

  components: {
    PaymentPageNavigation,
    PaymentForm,
    PaymentFormPreloader,
    Portal,
    PortalTarget,
    StickySidebar,
    PaymentSummary,
    PaymentSummaryDropdown,
    SummaryPreloader,
  },
  mixins: [checkout],

  data() {
    return {
      loadingPaymentInfo: false,
      $vueInstance: null as Vue | null,
      crossSellMixedResultsFlagEnabled: false,
      crossSellOnLoungesFlagEnabled: false,
      bundlesPrePurchase: false,
    }
  },

  computed: {
    ...mapState(useCartCheckoutStore, {
      readCartItems: 'readCartItems',
      readCartBundle: 'readCartBundle'
    }),
    isOnTravelExtrasPage(): boolean {
      return this.$route.name === ROUTE_NAMES.travel_extras
    },
    ...mapState(useDiscountCodeStore, {
      discountCode: 'discountCode',
      discountObject: 'discountObject',
      discountQuery: 'discountQuery',
    }),
    ...mapState(usePaymentFormStore, {
      readPaymentFormData: 'readPaymentFormData',
    }),
    isMultiBasket(): boolean {
      return !!getAppVariable('is_multi_basket');
    },
    paymentSummaryDropdown(): boolean {
      return getFeatureFlag(FEATURE_FLAGS.CRO_840_ORDER_SUMMARY_DROPDOWN) === true;
    },
    displayNewSummaries() {
      return this['$launchDarkly'].variation(FEATURE_FLAGS.PT_1566_DISPLAY_NEW_PAYMENT_SUMMARIES);
    },
    cartItems() {
      return this.readCartItems;
    },
    paymentFormData(){
      return this.readPaymentFormData;
    },
    additionalFields() {
      return this.checkout_cartItems.length > 1
        ? this.checkout_multiCartAdditionalFields
        : this.checkout_additionalFields;
    },
    cancellationProtection(): CancellationProtection[] {
      // Always treat cancellationProtection as an array
      return this.checkout_multiCartCancellationProtection
    },
    // TO DO - Remove cancellationProtectionProductIds when 'ECOM-2801_BUNDLES-PRE-PURCHASE' feature flag is removed - no longer needed
    cancellationProtectionProductIds(): string[] {
      return this.cancellationProtection
        .filter((item) => item?.is_available)
        .map((item) => item?.product_id);
    },
    cancellationProtectionNonBundleProducts(): string[] {
      return this.cancellationProtection
        .filter((item) => !item?.is_bundle_product && item?.is_available)
        .map((item) => item?.product_id) || [];
    },
    isProtected(): boolean {
      return this.cartItems?.some((item: CartItem) => item.is_protected);
    },
    cancellationPolicies(): CancellationPolicy[] | CartItem['inventory_option']['cancellation_policies'][] {
      return this.checkout_multiCartCancellationPolicies
    },
    amendmentPolicies(): AmendmentPolicy[] | CartItem['inventory_option']['amendment_policies'][] {
      return this.checkout_multiCartAmendmentPolicies
    },
    isBookingFeeCharged(): boolean {
      return this.checkout_bookingFee?.is_enabled || false;
    },
    bundle() {
      return this.readCartBundle;
    },
    isBundleCancellationProtectionAutoAdded(): boolean {
      return this.readCartBundle?.is_cancellation_protection_auto_add || false;
    },

    /**
     * The travel extras page is active if:
     * - the feature flag EM-369-CROSS-SELL-MIXED-RESULTS is active
     * - the cross sell flow is enabled by the partner setup
     * - the `skippedExtrasStep` flag from local storage does not match the current cart token
     */
    travelExtrasPageActive() {
      const { isCrossSellFlowEnabled } = usePartnerConfigHelper();

      const skippedExtrasStep = (localStorage.getItem('skippedExtrasStep') ?? '') === this.checkout_cartToken;
      const crossSellFlagEnabled = getAppHeroProduct(APP_HERO_PRODUCTS.LOUNGES) === APP_HERO_PRODUCTS.LOUNGES
        ? this.crossSellOnLoungesFlagEnabled
        : this.crossSellMixedResultsFlagEnabled;

      return isCrossSellFlowEnabled
        && crossSellFlagEnabled === true
        && !skippedExtrasStep;
    },
  },
  created() {
    this.commitCartItems([]);
  },

  mounted() {
    this.$vueInstance = this.$root;
    const { skipExtras: skippedExtrasFromRoute, ...remainingQuery } = this.$route.query;
    const skippedExtrasFromLocalStorage = localStorage.getItem('skippedExtrasStep') === this.checkout_cartToken;
    const skippedExtras = (
      skippedExtrasFromRoute === 'true'
      || skippedExtrasFromLocalStorage
    );
    if (skippedExtras) {
      localStorage.setItem('skippedExtrasStep', this.checkout_cartToken);
      this.$router.replace({ query: remainingQuery });
    } else {
      localStorage.setItem('skippedExtrasStep', '');
    }
  },

  methods: {
    ...mapActions(usePreloaderControllerStore, {
      commitUpdatePreloader: 'updatePreloader',
    }),

    ...mapActions(useCartCheckoutStore, {
      commitCartItems: 'commitCartItems',
      commitCartBundle: 'commitCartBundle',
      commitUpdateCancellationProtection: 'commitUpdateCancellationProtection',
    }),

    ...mapActions(useDiscountCodeStore, {
      storeDiscountCode: 'storeDiscountCode',
      storeDeepLinkCode: 'storeDeepLinkCode',
      clearDiscountCode: 'clearDiscountCode'
    }),

    cartHasExtrasProduct() {
      return this.cartItems?.some(
        (item: CartItem) => item.product_code === PRODUCT_TYPES.EXTRAS
      );
    },
    async getCart() {
      try {
        const cartItems = (await fetchCartItems(
          this.checkout_cartToken
        )) as Cart;
        const deposit = useDepositStore()
        deposit.updateCartDepositTotal({
          ...cartItems.payable,
        });
        deposit.updateItemDeposit(cartItems.items)
        this.commitCartItems(cartItems.items);
        if (cartItems.bundle) {
            this.commitCartBundle({
              ...cartItems.bundle,
              items: [...cartItems.items.filter((item) => item.bundle_id === cartItems.bundle.id)],
          });
        }
        // now we get a successful response even for a non-existent cart
        // to check if cart not empty we look at the length of the array
        if (cartItems.items.length) {
          // if we have discount code applied to the cart – put it to store to be displayed
          const discountAppliedToCart = parseDiscountCodeData(cartItems);
          if (discountAppliedToCart) {
            this.storeDiscountCode(discountAppliedToCart);
          }

          const {
            items,
            valid_till,
            is_valid,
            totals,
            original_amounts,
            booking_fee,
          } = cartItems;

          if (deposit.shouldShowDepositInformation) {
            totals['total'] = cartItems.payable.pay_now;
          }

          const item = items[0];
          const couponCode = item.search_criteria?.parking?.coupon_code;
          this.handleCouponCode(items, couponCode);

          this.checkout_cartItems = items;
          this.checkout_originalAmounts = original_amounts;
          this.checkout_bookingFee = booking_fee;
          this.checkout_total = totals;
          // @ts-ignore – Mixin method
          this.checkout_revalidateCartRecursive(is_valid, true, valid_till);

          const { isCrossSellFlowEnabled } = usePartnerConfigHelper();
          const { airport_code = '', terminal = '' } = (item?.search_criteria?.[item?.product_code] ?? { airport_code: '', terminal: '' });
          if (isCrossSellFlowEnabled && airport_code) {
            identifyWithNewContext(
              {
                'search-criteria': {
                  key: `criteria-${airport_code}-${terminal ?? 'dontKnow'}`,
                  kind: 'search-criteria',
                  airport: airport_code,
                  terminal: terminal ?? 'dontKnow',
                },
              },
            ).then(flagSet => {
              if (!flagSet) return;
              this.crossSellMixedResultsFlagEnabled = flagSet['EM-369-CROSS-SELL-MIXED-RESULTS'];
              this.crossSellOnLoungesFlagEnabled = flagSet['EM_782_CROSS_SELL_ON_LOUNGES'];
              this.bundlesPrePurchase = flagSet['ECOM-2801_BUNDLES-PRE-PURCHASE'];
              this.$launchDarkly.track('em_369_begin_checkout');
              if (isInExperiment('EM-369-CROSS-SELL-MIXED-RESULTS')) {
                this.$gtm.push({
                  event: 'launchdarkly',
                  launchDarklyFlagName: 'EM-369-CROSS-SELL-MIXED-RESULTS',
                  launchDarklyFlagValue: this.crossSellMixedResultsFlagEnabled,
                });
              }
            })
          }

          this.$global_disableContentPreloader();

          // @ts-ignore
          const query = this.checkout_getQueryForGoBack();
          if (this.isParkingProduct) {
            // If multiple POIs are available, we need to get the correct one
            const poi = getAppVariable('pois').find(
              (poi: POI) => poi.code === query.airport
            );

            this.$gtm.push(
              gaDataLayer({
                airportCode: poi.code,
                airportName: poi.name,
                partnerName: getAppVariable('partner_name'),
                siteLanguage: getAppVariable('default_language'),
                airportTerminalId: query.terminal,
                parkingDepartureDate: query.entryDate,
                parkingDepartureTime: query.entryTime,
                parkingReturnDate: query.exitDate,
                parkingReturnTime: query.exitTime,
                couponVoucher: query.discount || null,
                carParkName: this.checkout_orderName,
              })
            );
          }

          if (this.isLoungesProduct) {
            this.$gtm.push(
              gaDataLayerLounges(item.search_criteria?.lounges, {
                loungeName: this.checkout_orderName,
                loginStatus: this.$auth.isAuthenticated || false,
                userId: this.$auth.user?.sub || '',
              })
            );
          }
          const trackingItems = [...items.map((item) => ({
            ...item,
            bundle_name: cartItems.bundle?.name || '',
          }))];

          // TO DO - Remove when 'ECOM-2801_BUNDLES-PRE-PURCHASE' feature flag is no longer needed
          if (!this.bundlesPrePurchase) {
            if (items.some((item) => item.is_protected)) {
              this.updateCancellationProtection();

              // @ts-ignore - Mixin method
              items.forEach((item) => {
                trackingItems.push(this.checkout_createCancellationProtectionCartItem(item));
              });
            }
          }

          const metaIndex = item?.meta?.filter?.index;
          const metaItemListName = item?.meta?.filter?.item_list_name;
          const bookingFee = booking_fee?.amount;

          const params =
            metaIndex && metaItemListName
              ? [this.$gtm, trackingItems, bookingFee, metaIndex, metaItemListName]
              : [this.$gtm, trackingItems, bookingFee];

          if (this.isLoungesProduct) {
            params.push('lounges');
          }

          // @ts-ignore - Wrong product type
          trackBeginCheckout(...params);

          // Track generic LaunchDarkly 'begin_checkout' event
          this.$launchDarkly.track('begin_checkout');
        } else {
          // @ts-ignore - Mixin method
          this.checkout_showTechnicalIssueError();
        }
      } catch (error: unknown) {
        let errorResponse = '';
        try {
          errorResponse = JSON.parse(error.message);
        } catch (parseError) {}

        // Session expired
        if (errorResponse?.status === StatusCodes.GONE) {
          // @ts-ignore - Mixin method
          this.checkout_openExpiredModal();
          return;
        }

        // @ts-ignore - Mixin method
        this.checkout_showTechnicalIssueError();
      }
    },

    handleCouponCode(items, couponCode) {
      const discountApplied = items.some(item => item?.discount?.code);
      if (typeof couponCode === 'string' && couponCode && discountApplied) {
        this.storeDeepLinkCode(couponCode);
      } else {
        this.storeDeepLinkCode('');
      }
    },

    handlePaymentCompleted(payload: {
      payload: HandlePaymentCompletePayload;
      userDetails: HandlePaymentCompleteUser;
      metaData: HandlePaymentCompleteMetaData;
    }) {
      if (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE &&
        this.checkout_orderTotal !== 0
      ) {
        this.continueBooking(ContinueBookingStatuses.Successful);
      } else {
        this.createBooking(payload);
      }
    },

    handleStripePaymentIntentPayload(payload: {
      payload: HandlePaymentCompletePayload;
      userDetails: HandlePaymentCompleteUser;
      metaData: HandlePaymentCompleteMetaData;
    }) {
      this.createBooking(payload);
    },

    createBooking({
      payload,
      userDetails,
      metaData,
    }: {
      payload: HandlePaymentCompletePayload;
      userDetails: HandlePaymentCompleteUser;
      metaData: HandlePaymentCompleteMetaData;
    }) {
      const startTime = performance.now()
        const getOutboundFlight = (userDetails: HandlePaymentCompleteUser, cartItem: CartItem) => {
        // note that this has a single use-case - for TimeSlot to automatically
        // include the flight number on the booking.
        type TimeslotCriteria = { airline_iata?: string, flight_number?: string } | undefined;
        const { flight_number, airline_iata } = cartItem.search_criteria?.[APP_HERO_PRODUCTS.FASTTRACK] as TimeslotCriteria ?? {};
        if (getFeatureFlag(FEATURE_FLAGS.NGQ_11_TIMESLOT) && airline_iata && flight_number) return `${airline_iata}${flight_number}`;
        return userDetails.outbound_flight;
      };
      const body = {
        cart_token: this.checkout_cartToken,
        amount: this.checkout_orderTotal,
        currency: this.checkout_currency,
        user: {
          ...userDetails,
          outbound_flight: getOutboundFlight(userDetails, this.checkout_cartItem),
        },
        ...(!isBookingPortal
          ? {
              gateway_data: this.checkout_getGatewayData(payload),
            }
          : {}),
        ...(isBookingPortal
          ? {
              meta: {
                ...metaData,
              },
            }
          : {}),
      };

      this.commitUpdatePreloader(this.$t('UI.fullscreenPreloaderMessages.nearlyThere'));
      if (isBookingPortal) {

        handleBookingOrderWithoutPayment(this.checkout_cartToken, body)
          .then(() => {
            this.$openModal('GlobalModalError', {
              header: this.$t('UI.modalSuccess.bookingSuccessfullyUpdated.header'),
              body: this.$t('UI.modalSuccess.bookingSuccessfullyUpdated.body', { email: body.user.email || '' }),
              isSuccess: true,
              btnText: this.$t('UI.button.continue'),
            }).then(() => this.logoutBookingPortal());
          }).then(() => {
            addCustomMetric("tracking", "Booking Portal Metric: Success", {
              event: 'BookingSuccess'
            },  startTime, 'booking_service');
          })
           // @ts-ignore mixin method
          .catch(() => {
            this.checkout_showBookingPortalTechnicalError();
            this.commitUpdatePreloader('');
      })
      } else {
        handlePayment(this.checkout_cartToken, body)
          .then((data: PusherEvent) => {
            // @ts-ignore mixin variable
            this.checkout_forwardInfo = data.forward;

            if (getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE && this.checkout_orderTotal !== 0) {
              // @ts-ignore mixin property
              this.checkout_paymentsConfig.token = data.payment_client_secret;
              // @ts-ignore mixin property
              this.checkout_paymentIntentUpdated = true;
            } else {
              this.continueBooking(ContinueBookingStatuses.Successful);
            }
          })
          .catch((error) => {
            if (this.checkout_isWindcavePayment) {
              this.checkout_handleVoid(payload);
            }

            this.checkout_handlePaymentError(error);
          });
      }
    },

    continueBooking(status: string) {
      const startTime = performance.now()
      const body = {
        status,
        // @ts-ignore mixin variable
        forward: this.checkout_forwardInfo,
      };
      continueOriginalBooking(this.checkout_cartToken, body)
        .then(
          ({
            management_token,
            order_reference,
          }: {
            management_token: { token: string; expiration: number };
            order_reference: string;
          }) => {
            // @ts-ignore - Mixin method
            this.checkout_trackOnBookingComplete(
              {
                id: order_reference,
                affiliation: getAppVariable('partner_name'),
                revenue: this.checkout_orderTotal,
                coupon: '',
              },
              {
                name: this.checkout_orderName,
                id: this.checkout_cartItem.id,
                price: this.checkout_orderTotal,
                category: this.checkout_cartItem.product_code,
                quantity: 1, // Quantity, set to 1 for MVP
              }
            );

            // @ts-ignore - Mixin method
            this.checkout_onSuccessCleaningStorage();
            // @ts-ignore - Mixin method
            this.checkout_setCookiesAndGoToReceipt(
              management_token,
              'BOOKING_RECEIPT'
            );
          }
        ).catch((error: { errorType: string; error: string; type: string }) => {
          if (status === ContinueBookingStatuses.Failed) {
            this.checkout_forwardInfo = null;
            this.commitUpdatePreloader('');
          } else {
            // @ts-ignore mixin method
            this.checkout_handlePaymentError(error);
          }
        });
    },

    updateItems(items: CartItem[] | []) {
      this.checkout_cartItems = items;
    },

    updateOrderTotals(orderTotals: Cart['totals']) {
      this.checkout_total = orderTotals;
    },

    handleAddDiscountError() {
      // If we receive an error when adding a discount and another discount was added before that
      // We return the values to the original (without discount) from 'original_amounts' property

      if (this.checkout_cartItem.discount && this.discountCode) {
        this.clearDiscountCode();
        if (this.isParkingProduct) {
          this.checkout_total = this.checkout_originalAmounts;
          this.checkout_cartItems = [
            {
              ...this.checkout_cartItem,
              totals: this.checkout_cartItem.original_amounts.totals,
              fees: this.checkout_cartItem.original_amounts.fees,
              taxes: this.checkout_cartItem.original_amounts.taxes,
              discount: null,
            },
          ];
        } else if (this.isLoungesProduct) {
          this.checkout_total = this.checkout_originalAmounts;
          this.checkout_cartItems = this.cartItems
        }
      }
    },

    showPaymentFailedModal() {
      setTimeout(() => {
        this.$vueInstance.$openModal('GlobalModalError', {
          header: this.$vueInstance.$t('UI.modalError.paymentError.header'),
          body: this.$vueInstance.$t('UI.modalError.paymentError.body'),
          buttons: [
            {
              btnText: this.$vueInstance.$t('shared.buttons.close'),
            },
          ],
        });
      }, 500);
    },

    handleWidgetError() {
      // @ts-ignore mixin variable
      if (this.checkout_forwardInfo) {
        this.continueBooking(ContinueBookingStatuses.Failed);
        this.checkout_paymentIntentUpdated = false;
        this.showPaymentFailedModal();
      }
    },

    async addCancellationProtection(): Promise<void> {
      this.loadingPaymentInfo = true;

      // TO DO - Remove if/else when 'ECOM-2801_BUNDLES-PRE-PURCHASE' feature flag is no longer needed
      if (this.bundlesPrePurchase) {
        if (this.isBundleCancellationProtectionAutoAdded) {
          await this.toggleCancellationProtectionSpecificItems(this.cancellationProtectionNonBundleProducts, true);
        } else {
          await this.toggleCancellationProtectionWholeCart(true);
        }
      } else {
        await this.toggleCancellationProtectionSpecificItems(this.cancellationProtectionProductIds, true);
      }

      this.loadingPaymentInfo = false;
      if (this.checkout_isWindcavePayment) {
        await this.checkout_handleWindcaveReload();
      }
    },

    async removeCancellationProtection(): Promise<void> {
      this.loadingPaymentInfo = true;

      // TO DO - Remove if/else when 'ECOM-2801_BUNDLES-PRE-PURCHASE' feature flag is no longer needed
      if (this.bundlesPrePurchase) {
        if (this.isBundleCancellationProtectionAutoAdded) {
          await this.toggleCancellationProtectionSpecificItems(this.cancellationProtectionNonBundleProducts, false);
        } else {
          await this.toggleCancellationProtectionWholeCart(false);
        }
      } else {
        await this.toggleCancellationProtectionSpecificItems(this.cancellationProtectionProductIds, false);
      }

      this.loadingPaymentInfo = false;
      if (this.checkout_isWindcavePayment) {
        await this.checkout_handleWindcaveReload();
      }
    },

    // TO DO - Remove updateCancellationProtection when 'ECOM-2801_BUNDLES-PRE-PURCHASE' feature flag is no longer needed
    updateCancellationProtection(): void {
      const productIdsWithoutCancellationProtection = this.cancellationProtectionProductIds.filter(productId => {
        const product = this.cartItems.find(item => item.id === productId);
        return product && !product.is_protected;
      });
      this.toggleCancellationProtectionSpecificItems(productIdsWithoutCancellationProtection, true);
    },

    async toggleCancellationProtectionSpecificItems(productIds: string[], isProtected: boolean): Promise<void> {
      const toggleCancellationProtectionRecursive = async (index: number) => {
        if (index >= productIds.length) {
          return;
        }

        const productId = productIds[index];
        const { totals, payable } = await updateCancellationProtectionRequestSingleItem<
            ExtrasCancellationProtectionProduct,
            Cart['totals'],
            Cart['payable']
        >(productId, this.checkout_cartToken, {
          is_protected: isProtected,
        });

        this.checkout_total = totals;
        const deposit = useDepositStore();
        deposit.updateCartDepositTotal({...payable });
        this.commitUpdateCancellationProtection({ productId, isProtected });

        await toggleCancellationProtectionRecursive(index + 1);
      };

      await toggleCancellationProtectionRecursive(0);
    },

    async toggleCancellationProtectionWholeCart(isProtected: boolean): Promise<void> {
        const { items, totals, payable } = await updateCancellationProtectionRequestWholeCart<
            ExtrasCancellationProtectionProduct,
            Cart['totals'],
            Cart['payable']
        >(this.checkout_cartToken, {
          is_protected: isProtected,
        });

        this.checkout_total = totals;
        const deposit = useDepositStore();
        deposit.updateCartDepositTotal({...payable });
        Array.isArray(items) && items.forEach((item) => {
          this.commitUpdateCancellationProtection({ productId: item.id, isProtected: item.is_protected });
        });
        this.updateItems(items);
    }
  },
});
